import type { Guild } from '@/types/api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from '@heroui/react';
import { ChatBubbleIcon, HashIcon, ServerIcon, ChevronDownIcon, PlusIcon } from '@/components/icons';
import { ServerModal } from '@/components/ServerModal';
import { IdentityAvatar } from './IdentityAvatar';
import { useDataStore } from '@/stores/dataStore';

/** Always-visible destinations in the platform dock; never connects voice. */
export function CompactNavigation({ channels, onChats, onChannels, onGuild }: {
  channels: boolean; onChats: () => void; onChannels: () => void; onGuild: (id: string) => void;
}) {
  const { t } = useTranslation();
  const guildId = useDataStore(s => s.currentGuildId);
  const guilds = useDataStore(s => s.guilds);
  const order = useDataStore(s => s.guildOrder);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [manage, setManage] = useState(false);
  const guild = guildId ? guilds[guildId] : null;
  const available = order.map(id => guilds[id]).filter((g): g is Guild => Boolean(g)).filter(g => g.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()));
  return <><ServerModal open={manage} onClose={() => setManage(false)} initialTab="join"/><nav className="studio-compact-nav" aria-label={t('compactNav.label')}>
    <div className="studio-compact-tabs">
      <button type="button" aria-pressed={!channels} onClick={onChats}><ChatBubbleIcon className="size-4"/>{t('compactNav.chats')}</button>
      <button type="button" aria-pressed={channels} onClick={onChannels}><HashIcon className="size-4"/>{t('compactNav.channels')}</button>
    </div>
    <Popover isOpen={open} onOpenChange={value => { setOpen(value); if (!value) setQuery(''); }}>
      <Popover.Trigger className="studio-compact-server" aria-label={t('compactNav.selectServer')}>
        {guild ? <IdentityAvatar key={guild.id} id={guild.id} name={guild.name} src={guild.iconUrl} size="xs" className="studio-guild-avatar shrink-0"/> : <ServerIcon className="size-4 shrink-0"/>}<span className="min-w-0 flex-1 truncate text-left">{guild?.name ?? t('compactNav.servers')}</span><ChevronDownIcon className="size-4 shrink-0"/>
      </Popover.Trigger>
      <Popover.Content placement="bottom end"><Popover.Dialog aria-label={t('compactNav.selectServer')} className="studio-compact-picker w-72 max-w-[calc(100vw-24px)] p-2">
        <input autoFocus className="studio-compact-search mb-2 w-full rounded-md bg-background px-3 py-2 text-sm" aria-label={t('compactNav.searchServer')} placeholder={t('compactNav.searchServer')} value={query} onChange={e => setQuery(e.target.value)}/>
        <div className="max-h-64 overflow-y-auto">
          {available.map(g => <button key={g.id} type="button" className="studio-compact-server-row" aria-current={g.id === guildId ? 'true' : undefined} onClick={() => { onGuild(g.id); setOpen(false); setQuery(''); }}>
            <IdentityAvatar id={g.id} name={g.name} src={g.iconUrl} size="sm" className="studio-guild-avatar"/><span className="truncate">{g.name}</span>
          </button>)}
          {!available.length && <p className="p-3 text-sm text-muted">{t('compactNav.noServers')}</p>}
        </div>
        <button type="button" className="studio-compact-server-row" onClick={() => { setOpen(false); setManage(true); }}><PlusIcon className="size-4"/>{t('compactNav.manageServers')}</button>
      </Popover.Dialog></Popover.Content>
    </Popover>
  </nav></>;
}
